import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { inject, observer } from "mobx-react";
import session from "../Utils/session";
import config from '../Utils/config';

import Title from "../Components/Title";
import PlaylistCard from "../Components/PlaylistCard";
import CompactUserCard from "../Components/CompactUserCard";
import Button from "../Components/Button";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePlausible } from "../hooks/usePlausible";

// Format numbers to compact notation (1k, 13.3k, 1.2m, etc.)
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'm';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return num.toString();
};

const UserProfile = ({ UserProfileStore, PlaylistStore, onBack }) => {
  const { username } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { trackEvent } = usePlausible();

  useEffect(() => {
    if (username === session.getUsername()) {
      navigate("/app/me");
      return;
    }
    if (
      !UserProfileStore.loaded ||
      !UserProfileStore.profile ||
      UserProfileStore.profile.username !== username
    )
      UserProfileStore.load(username);
  }, [UserProfileStore, username, navigate]);

  const handlePlaylistSelect = (loadPlaylistFn) => {
    loadPlaylistFn();
    if (isMobile && onBack) {
      onBack();
    }
  };

  const profile = UserProfileStore.profile;

  return (
    <div className="flex-1 flex flex-col bg-primary">
      {/* Profile Not Found State */}
      {UserProfileStore.loaded && !profile && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <svg className="w-24 h-24 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-3xl font-bold text-gray-300 mb-2">
              Profile not found
            </h2>
            <p className="text-gray-400">
              The user you're looking for doesn't exist
            </p>
          </div>
        </div>
      )}

      {/* Profile Content */}
      {UserProfileStore.loaded && profile && (
        <>
          {/* Profile Header Card */}
          <div className="bg-primary border-b border-neutral-800">
            <div className="max-w-7xl mx-auto p-3 md:p-5 lg:p-6">
              <div className="flex flex-row items-start gap-3 md:gap-5">
                {/* Profile Picture */}
                <div className="flex-shrink-0">
                  <ProfilePic
                    src={`${config.serverUrl}/users/profile/${username}/avatar`}
                  />
                </div>

                {/* Profile Details */}
                <div className="flex-1 flex flex-col items-start gap-2 w-full">
                  {/* Username and Follow Button */}
                  <div className="flex items-center justify-between w-full gap-3">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white">
                      {username}
                    </h1>
                    <Button
                      onClick={() => {
                        const wasFollowing = UserProfileStore.following;
                        UserProfileStore.toggleFollow();
                        trackEvent(wasFollowing ? 'Unfollow User' : 'Follow User', {
                          username: username,
                        });
                      }}
                      variant={UserProfileStore.following ? "secondary" : "primary"}
                      size="sm"
                    >
                      {UserProfileStore.following ? (
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Following
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Follow
                        </span>
                      )}
                    </Button>
                  </div>

                  {/* Stats */}
                  <div className="flex flex-wrap items-center justify-start gap-3 md:gap-5">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-semibold text-white">{formatNumber(profile.playCount)}</span>
                      <span className="text-xs md:text-sm text-gray-400">Plays</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                      <span className="font-semibold text-white">{formatNumber(profile.followedBy.length)}</span>
                      <span className="text-xs md:text-sm text-gray-400">Followers</span>
                    </div>
                    <div className="hidden md:flex items-center gap-1.5">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      <span className="font-semibold text-white">{formatNumber(profile.following.length)}</span>
                      <span className="text-xs md:text-sm text-gray-400">Following</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="max-w-7xl mx-auto w-full p-3 md:p-5 lg:p-6 space-y-5 md:space-y-6">
            {/* Playlists Section */}
            <section>
              <Title label="Playlists" />
              {profile.playlists.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 md:gap-4 mt-3">
                  {profile.playlists.map((list) => (
                    <PlaylistCard
                      title={list.title}
                      key={list.title}
                      thumbnails={list.thumbnailUrls}
                      onSelect={() => handlePlaylistSelect(() => PlaylistStore.loadPlaylist(list.id))}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center p-8 bg-secondary border border-neutral-800 rounded-md mt-4">
                  <p className="text-gray-400 text-center">
                    {username} has no public playlists
                  </p>
                </div>
              )}
            </section>

            {/* Followers Section */}
            <section>
              <Title label="Followers" />
              {profile.followedBy.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3 mt-3">
                  {profile.followedBy.map((user) => (
                    <CompactUserCard
                      username={user.username}
                      key={user.username}
                      online={user.online}
                      image={`${config.serverUrl}/users/profile/${user.username}/avatar`}
                      onSelect={() => {
                        navigate(`/app/u/${user.username}`);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center p-8 bg-secondary border border-neutral-800 rounded-md mt-4">
                  <p className="text-gray-400 text-center">
                    {username} has no followers yet
                  </p>
                </div>
              )}
            </section>

            {/* Following Section */}
            <section>
              <Title label="Following" />
              {profile.following.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3 mt-3">
                  {profile.following.map((user) => (
                    <CompactUserCard
                      username={user.username}
                      key={user.username}
                      online={user.online}
                      image={`${config.serverUrl}/users/profile/${user.username}/avatar`}
                      onSelect={() => {
                        navigate(`/app/u/${user.username}`);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center p-8 bg-secondary border border-neutral-800 rounded-md mt-4">
                  <p className="text-gray-400 text-center">
                    {username} is not following anybody yet
                  </p>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

const ProfilePic = ({ src }) => {
  return (
    <img
      src={src}
      alt="Profile"
      className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full border-3 md:border-4 border-white shadow-lg object-cover"
    />
  );
};

export default inject("UserProfileStore", "PlaylistStore")(observer(UserProfile));
